'use client';

import { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useDispatch, useSelector } from 'react-redux';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { api } from '../../configurations/api';
import { campaignDuplicateUrl } from '../../configurations/api/url';
import { httpRequest } from '../../redux/types';
import { showToastMessage } from '../../utils/AppUtils';
import { formatCurrency } from '../../constants/currency';
import { computeCampaignFeeBreakdown } from '../../constants/campaignBudgetFees';
import type { ICampaign, IDeliverableRound, InfluencerTier } from '../../redux/buzzbridge/types';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import {
    activeInfluencerTiersSelector,
    ugcBudgetConfigSelector,
    ugcCampaignFeeConfigSelector,
} from '../../selectors/selectors';
import {
    campaignHostCreationAccessSelector,
    campaignHostPricingSelector,
    hasActiveCampaignHostPlanSelector,
} from '../../redux/campaignHostSubscription/selectors';
import { fetchCampaignHostCreationAccess } from '../../redux/campaignHostSubscription/actions';
import {
    clampCreatorsToPlanLimit,
    formatCreationAccessMaxCreatorsLabel,
    getCreationAccessMaxCreators,
    getCreationBillingCopy,
    isOverCreatorPlanLimit,
    parseCampaignCreationEntitlementError,
} from '../../utils/campaignHostCreationEntitlements';
import { getUgcBudgetFieldSx, parseBudgetInput, ugcBudgetDatePickerSx } from './ugcCampaignFormHelpers';
import { UgcFormFieldCard, UgcInfluencerTierCards } from './ugcCampaignTargetingUi';
import { CustomDatePicker } from '../../components/DatePicker/CustomDatePicker';
import dayjs from 'dayjs';
import {
    BRAND_LUXE_MUTED,
    BRAND_LUXE_PRIMARY,
    BRAND_ON_PRIMARY_FIXED,
    BRAND_PRIMARY_CONTAINER,
    BRAND_SURFACE_CONTAINER,
    BRAND_SURFACE_CONTAINER_LOW,
} from './brandDashboardTokens';

const labelSx = {
    fontSize: 12,
    fontFamily: FONT_ENUM.MAIN_BOLD,
    color: BRAND_LUXE_MUTED,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.08em',
    mb: 1,
};

/** Prefer source dates when still in the future; otherwise shift forward from tomorrow keeping duration. */
function seedDuplicateTimeline(
    campaign: Pick<ICampaign, 'start_date' | 'end_date'> & {
        delivery_date?: string;
        recurrent_deliverables?: boolean;
        deliverable_rounds?: IDeliverableRound[];
    },
) {
    const tomorrow = dayjs().add(1, 'day').startOf('day');
    const srcStart = campaign.start_date ? dayjs(campaign.start_date) : null;
    const srcEnd = campaign.end_date ? dayjs(campaign.end_date) : null;
    const srcDelivery = (campaign as any).delivery_date ? dayjs((campaign as any).delivery_date) : null;
    const startOk = srcStart?.isValid() && srcStart.isAfter(tomorrow.subtract(1, 'day'));
    const endOk = srcEnd?.isValid() && srcEnd.isAfter(tomorrow.subtract(1, 'day'));

    const isRecurrent = Boolean((campaign as any).recurrent_deliverables);
    const srcRounds: IDeliverableRound[] = (campaign as any).deliverable_rounds || [];

    function shiftRounds(newStart: dayjs.Dayjs): IDeliverableRound[] {
        if (!isRecurrent || !srcRounds.length || !srcStart?.isValid()) return srcRounds;
        const offsetDays = newStart.diff(srcStart, 'day');
        return srcRounds.map(r => ({
            ...r,
            _id: undefined,
            delivery_date: r.delivery_date
                ? dayjs(r.delivery_date).add(offsetDays, 'day').toISOString()
                : r.delivery_date,
        }));
    }

    if (startOk && endOk && srcEnd!.isAfter(srcStart!)) {
        const deliveryOk =
            !isRecurrent &&
            srcDelivery?.isValid() &&
            srcDelivery.isAfter(srcStart!.add(7, 'day').subtract(1, 'day')) &&
            srcDelivery.isBefore(srcEnd!);
        return {
            start: srcStart!.toISOString(),
            end: srcEnd!.toISOString(),
            delivery: deliveryOk ? srcDelivery!.toISOString() : '',
            rounds: shiftRounds(srcStart!),
        };
    }

    const durationDays =
        srcStart?.isValid() && srcEnd?.isValid() && srcEnd.isAfter(srcStart)
            ? Math.max(7, srcEnd.diff(srcStart, 'day'))
            : 30;
    const start = tomorrow;
    const end = tomorrow.add(durationDays, 'day');

    let delivery = '';
    if (!isRecurrent && srcDelivery?.isValid() && srcStart?.isValid()) {
        const offset = srcDelivery.diff(srcStart, 'day');
        const shifted = start.add(offset, 'day');
        if (shifted.isAfter(start.add(7, 'day').subtract(1, 'day')) && shifted.isBefore(end)) {
            delivery = shifted.toISOString();
        }
    }

    return { start: start.toISOString(), end: end.toISOString(), delivery, rounds: shiftRounds(start) };
}

export function isUgcCampaignDuplicable(campaign: Pick<ICampaign, 'status' | 'end_date'>): boolean {
    if (campaign.status === 'paused') return true;
    if (campaign.end_date && new Date(campaign.end_date).getTime() < Date.now()) return true;
    return false;
}

export function DuplicateCampaignDialog({
    open,
    campaign,
    onClose,
    onDuplicated,
}: {
    open: boolean;
    campaign: ICampaign | null;
    onClose: () => void;
    onDuplicated: (campaignId: string) => void;
}) {
    const { themeStyle } = useThemeContext();
    const t = theme[themeStyle];
    const dispatch = useDispatch();
    const budgetFieldSx = useMemo(() => getUgcBudgetFieldSx(t), [t]);
    const activeTiers = useSelector(activeInfluencerTiersSelector);
    const ugcFeeConfig = useSelector(ugcCampaignFeeConfigSelector);
    const ugcBudget = useSelector(ugcBudgetConfigSelector);
    const hasPlan = useSelector(hasActiveCampaignHostPlanSelector);
    const creationAccess = useSelector(campaignHostCreationAccessSelector);
    const hostPricing = useSelector(campaignHostPricingSelector);
    const access = creationAccess ?? hostPricing?.creation_access ?? null;
    const maxCreators = getCreationAccessMaxCreators(access);
    const creatorCapLabel = formatCreationAccessMaxCreatorsLabel(access);
    const billingCopy = getCreationBillingCopy(access);

    const [title, setTitle] = useState('');
    const [budgetInput, setBudgetInput] = useState('');
    const [participantsInput, setParticipantsInput] = useState('1');
    const [tier, setTier] = useState<InfluencerTier>('micro');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [deliveryDate, setDeliveryDate] = useState('');
    const [deliverableRounds, setDeliverableRounds] = useState<IDeliverableRound[]>([]);
    const [submitting, setSubmitting] = useState(false);
    const [basePayInput, setBasePayInput] = useState('');
    const [commissionRateInput, setCommissionRateInput] = useState('');
    const [displayPoolInput, setDisplayPoolInput] = useState('');

    const isRecurrent = Boolean((campaign as any)?.recurrent_deliverables);
    const isCommission = campaign?.campaign_type === 'commission';

    useEffect(() => {
        if (open) dispatch(fetchCampaignHostCreationAccess());
    }, [open, dispatch]);

    useEffect(() => {
        if (!open || !campaign) return;
        setTitle(`${campaign.title || 'Campaign'} (copy)`);
        setBudgetInput(campaign.total_budget != null ? Number(campaign.total_budget).toLocaleString('en-US') : '');
        setParticipantsInput(
            String(
                clampCreatorsToPlanLimit(
                    Number(campaign.total_participants_count || campaign.participants_count || 1),
                    maxCreators,
                ),
            ),
        );
        const seedTier = (campaign.influencer_tier as InfluencerTier) || 'micro';
        setTier(seedTier);
        const timeline = seedDuplicateTimeline(campaign);
        setStartDate(timeline.start);
        setEndDate(timeline.end);
        setDeliveryDate(timeline.delivery);
        setDeliverableRounds(timeline.rounds);
        const srcBase = (campaign as any).base_payment_per_creator_cents;
        setBasePayInput(srcBase != null ? String(Number(srcBase) / 100) : '');
        const srcRate = (campaign as any).commission?.rate;
        setCommissionRateInput(srcRate != null ? String(srcRate) : '');
        const srcPool = (campaign as any).commission?.display_pool_cents;
        setDisplayPoolInput(srcPool != null ? String(Number(srcPool) / 100) : '');
        // Intentionally omit maxCreators — clamp when access loads via the effect below.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, campaign]);

    useEffect(() => {
        if (maxCreators == null) return;
        const n = parseInt(participantsInput.trim(), 10);
        if (Number.isFinite(n) && n > maxCreators) {
            setParticipantsInput(String(maxCreators));
        }
    }, [maxCreators, participantsInput]);

    useEffect(() => {
        if (!activeTiers.length) return;
        if (!activeTiers.some(x => x.id === tier)) {
            setTier(activeTiers[0].id as InfluencerTier);
        }
    }, [activeTiers, tier]);

    const platforms = useMemo(
        () => (campaign?.platforms?.length ? campaign.platforms : ['instagram']),
        [campaign?.platforms],
    );
    const participantsCount = clampCreatorsToPlanLimit(
        Math.max(1, Math.floor(parseInt(participantsInput.trim(), 10) || 1)),
        maxCreators,
    );
    const overCreatorLimit = isOverCreatorPlanLimit(parseInt(participantsInput.trim(), 10) || 0, maxCreators);
    const budgetFormatted = parseBudgetInput(budgetInput);
    const isAppInstall = Boolean(campaign?.app_install?.enabled);
    const isPaid = campaign?.reward === 'paid' || campaign?.reward === 'both' || campaign?.reward == null;

    const feeBreakdown = useMemo(() => {
        if (!isPaid || isAppInstall) return null;
        return computeCampaignFeeBreakdown(
            {
                platforms,
                participantsCount,
                influencerTier: tier || activeTiers[0]?.id || 'micro',
                totalBudget: budgetFormatted,
                hostHasActiveSubscription: hasPlan,
            },
            ugcFeeConfig,
        );
    }, [isPaid, isAppInstall, platforms, participantsCount, tier, activeTiers, budgetFormatted, hasPlan, ugcFeeConfig]);

    const tierSummaryLabel = useMemo(() => {
        const tm = activeTiers.find(x => x.id === tier);
        return tm ? `${tm.name} (${tm.reach})` : feeBreakdown?.tier.name ?? tier;
    }, [activeTiers, tier, feeBreakdown?.tier.name]);

    const belowMinimum =
        feeBreakdown != null && budgetFormatted > 0 && budgetFormatted + 0.01 < feeBreakdown.minimumRequiredBudget;

    const handleSubmit = async () => {
        if (!campaign?._id) return;
        const total_budget = parseBudgetInput(budgetInput);
        if (!Number.isFinite(total_budget) || total_budget <= 0) {
            showToastMessage('Enter a valid budget', 'error');
            return;
        }
        if (overCreatorLimit) {
            showToastMessage(creatorCapLabel || 'Too many creators for your plan. Upgrade to add more.', 'error');
            return;
        }
        if (belowMinimum && feeBreakdown) {
            showToastMessage(`Budget must be at least ${formatCurrency(feeBreakdown.minimumRequiredBudget)}`, 'error');
            return;
        }
        if (!String(startDate || '').trim() || !String(endDate || '').trim()) {
            showToastMessage('Set campaign start and end dates', 'error');
            return;
        }
        if (dayjs(endDate).isBefore(dayjs(startDate), 'day')) {
            showToastMessage('End date must be on or after start date', 'error');
            return;
        }
        const isCommission = campaign?.campaign_type === 'commission';
        if (!isCommission && !isRecurrent) {
            if (!String(deliveryDate || '').trim()) {
                showToastMessage('Set a content delivery date', 'error');
                return;
            }
            const minDelivery = dayjs(startDate).add(7, 'day');
            if (dayjs(deliveryDate).isBefore(minDelivery, 'day')) {
                showToastMessage('Delivery date must be at least 7 days after start date', 'error');
                return;
            }
            if (!dayjs(deliveryDate).isBefore(dayjs(endDate), 'day')) {
                showToastMessage('Delivery date must be before end date', 'error');
                return;
            }
        }
        setSubmitting(true);
        try {
            const response = await api(
                campaignDuplicateUrl(campaign._id),
                httpRequest.POST,
                {
                    title: title.trim() || undefined,
                    total_budget,
                    participants_count: participantsCount,
                    influencer_tier: tier,
                    start_date: startDate,
                    end_date: endDate,
                    ...(isRecurrent
                        ? { recurrent_deliverables: true, deliverable_rounds: deliverableRounds }
                        : deliveryDate
                        ? { delivery_date: deliveryDate }
                        : {}),
                    ...(isCommission && basePayInput.trim() !== '' && {
                        base_payment_per_creator_cents: Math.round(parseFloat(basePayInput) * 100),
                    }),
                    ...(isCommission && commissionRateInput.trim() !== '' && {
                        commission_rate: parseFloat(commissionRateInput),
                    }),
                    ...(isCommission && displayPoolInput.trim() !== '' && {
                        commission_display_pool_cents: Math.round(parseFloat(displayPoolInput) * 100),
                    }),
                },
                1,
                8000,
            );
            const created = response?.data?.data?.data ?? response?.data?.data ?? null;
            const newId = created?._id ? String(created._id) : '';
            showToastMessage('Campaign duplicated as draft', 'success');
            onClose();
            if (newId) onDuplicated(newId);
        } catch (err: any) {
            const entitlement = parseCampaignCreationEntitlementError(err);
            showToastMessage(
                entitlement.message || err?.data?.message || err?.message || 'Could not duplicate campaign',
                'error',
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={submitting ? undefined : onClose}
            fullWidth
            maxWidth="md"
            PaperProps={{
                sx: {
                    bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                    border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: 3,
                    maxHeight: '92vh',
                },
            }}>
            <DialogTitle
                sx={{
                    fontFamily: FONT_ENUM.MAIN_BOLD,
                    color: BRAND_LUXE_PRIMARY,
                    fontSize: 20,
                    pr: 6,
                }}>
                Duplicate campaign
                <IconButton
                    aria-label="Close"
                    onClick={onClose}
                    disabled={submitting}
                    sx={{ position: 'absolute', right: 12, top: 12, color: BRAND_LUXE_MUTED }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: 0 }}>
                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                        fontSize: 13,
                        color: BRAND_LUXE_MUTED,
                        mb: 2.5,
                        lineHeight: 1.5,
                    }}>
                    Creates a new draft from this ended campaign. Adjust funding settings below.
                    {isAppInstall ? ' App-install campaigns keep the same attribution token.' : ''}
                </Typography>

                {billingCopy ? (
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            fontSize: 12,
                            color: 'rgba(255,255,255,0.55)',
                            mb: 2,
                            lineHeight: 1.5,
                        }}>
                        {billingCopy}
                    </Typography>
                ) : null}

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <UgcFormFieldCard>
                        <Typography sx={labelSx}>Title</Typography>
                        <TextField
                            fullWidth
                            value={title}
                            onChange={e => setTitle(e.target.value)}
                            placeholder="Campaign title"
                            sx={budgetFieldSx}
                        />
                    </UgcFormFieldCard>

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                            gap: 2,
                        }}>
                        <UgcFormFieldCard>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5 }}>
                                <Typography sx={{ ...labelSx, mb: 0 }}>Total budget</Typography>
                                <Tooltip
                                    arrow
                                    title="How much you’re willing to spend. Check the breakdown to confirm it covers creators and fees.">
                                    <InfoOutlinedIcon
                                        sx={{ fontSize: 16, color: 'rgba(255,255,255,0.45)', cursor: 'help' }}
                                    />
                                </Tooltip>
                            </Box>
                            <TextField
                                fullWidth
                                type="text"
                                inputMode="decimal"
                                placeholder="12,500"
                                value={budgetInput}
                                onChange={e => setBudgetInput(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Typography
                                                sx={{
                                                    color: BRAND_LUXE_PRIMARY,
                                                    fontWeight: 700,
                                                    fontSize: 20,
                                                }}>
                                                $
                                            </Typography>
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    ...budgetFieldSx,
                                    '& .MuiOutlinedInput-root': {
                                        ...budgetFieldSx['& .MuiOutlinedInput-root'],
                                        pl: 1,
                                    },
                                    '& .MuiInputBase-input': {
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: 20,
                                        color: t.primary_white,
                                    },
                                }}
                            />
                        </UgcFormFieldCard>

                        <UgcFormFieldCard>
                            <Typography sx={labelSx}>Number of creators</Typography>
                            {creatorCapLabel ? (
                                <Typography
                                    sx={{
                                        fontSize: 12,
                                        color: 'rgba(255,255,255,0.5)',
                                        mb: 1.5,
                                        lineHeight: 1.5,
                                    }}>
                                    {creatorCapLabel}
                                </Typography>
                            ) : null}
                            <TextField
                                fullWidth
                                type="number"
                                inputProps={{
                                    min: 1,
                                    step: 1,
                                    ...(maxCreators != null ? { max: maxCreators } : {}),
                                }}
                                placeholder={maxCreators != null ? `1–${maxCreators}` : 'e.g. 10'}
                                value={participantsInput}
                                onChange={e => {
                                    const n = parseInt(e.target.value, 10);
                                    if (!Number.isFinite(n)) {
                                        setParticipantsInput(e.target.value);
                                        return;
                                    }
                                    setParticipantsInput(String(clampCreatorsToPlanLimit(n, maxCreators)));
                                }}
                                error={overCreatorLimit}
                                helperText={
                                    overCreatorLimit ? creatorCapLabel || 'Too many creators for your plan.' : undefined
                                }
                                sx={{
                                    ...budgetFieldSx,
                                    '& .MuiInputBase-input': {
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: 20,
                                        color: t.primary_white,
                                    },
                                }}
                            />
                        </UgcFormFieldCard>
                    </Box>

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: isRecurrent ? '1fr 1fr' : '1fr 1fr 1fr' },
                            gap: 2,
                        }}>
                        <UgcFormFieldCard>
                            <Typography sx={labelSx}>Campaign start</Typography>
                            <CustomDatePicker
                                value={startDate || undefined}
                                onChange={val => {
                                    setStartDate(val || '');
                                    // Clear delivery if no longer 7+ days after new start
                                    if (!isRecurrent && deliveryDate && val) {
                                        const minDelivery = dayjs(val).add(7, 'day');
                                        if (dayjs(deliveryDate).isBefore(minDelivery, 'day')) {
                                            setDeliveryDate('');
                                        }
                                    }
                                }}
                                minDate={dayjs().add(1, 'day').toISOString()}
                                withTime={false}
                                withTimezone={false}
                                sx={ugcBudgetDatePickerSx}
                            />
                        </UgcFormFieldCard>
                        {!isRecurrent && (
                            <UgcFormFieldCard>
                                <Typography sx={labelSx}>Delivery date</Typography>
                                <CustomDatePicker
                                    value={deliveryDate || undefined}
                                    onChange={val => setDeliveryDate(val || '')}
                                    minDate={
                                        startDate && dayjs(startDate).isValid()
                                            ? dayjs(startDate).add(7, 'day').toISOString()
                                            : dayjs().add(8, 'day').toISOString()
                                    }
                                    maxDate={
                                        endDate && dayjs(endDate).isValid()
                                            ? dayjs(endDate).subtract(1, 'day').toISOString()
                                            : undefined
                                    }
                                    withTime={false}
                                    withTimezone={false}
                                    sx={ugcBudgetDatePickerSx}
                                />
                            </UgcFormFieldCard>
                        )}
                        <UgcFormFieldCard>
                            <Typography sx={labelSx}>Campaign end</Typography>
                            <CustomDatePicker
                                value={endDate || undefined}
                                onChange={val => {
                                    setEndDate(val || '');
                                    // Clear delivery if no longer before new end
                                    if (!isRecurrent && deliveryDate && val && !dayjs(deliveryDate).isBefore(dayjs(val), 'day')) {
                                        setDeliveryDate('');
                                    }
                                }}
                                minDate={
                                    startDate && dayjs(startDate).isValid()
                                        ? startDate
                                        : dayjs().add(1, 'day').toISOString()
                                }
                                withTime={false}
                                withTimezone={false}
                                sx={ugcBudgetDatePickerSx}
                            />
                        </UgcFormFieldCard>
                    </Box>
                    {isRecurrent && (
                        <Box
                            sx={{
                                p: 2,
                                borderRadius: 1.5,
                                bgcolor: 'rgba(255,201,101,0.06)',
                                border: '1px solid rgba(255,201,101,0.18)',
                            }}>
                            <Typography
                                sx={{
                                    fontSize: 12,
                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                    color: BRAND_LUXE_MUTED,
                                    lineHeight: 1.55,
                                }}>
                                This campaign has {deliverableRounds.length} recurring round
                                {deliverableRounds.length !== 1 ? 's' : ''}. Delivery dates have been shifted to match
                                the new start date. You can edit the rounds after duplicating.
                            </Typography>
                        </Box>
                    )}

                    <UgcFormFieldCard>
                        <Typography sx={labelSx}>Influencer tier</Typography>
                        <UgcInfluencerTierCards
                            tiers={activeTiers}
                            selectedTierId={tier}
                            onSelect={id => setTier(id as InfluencerTier)}
                        />
                    </UgcFormFieldCard>

                    {isCommission && (
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
                            <UgcFormFieldCard>
                                <Typography sx={labelSx}>Base pay per creator</Typography>
                                <TextField
                                    fullWidth
                                    type="text"
                                    inputMode="decimal"
                                    placeholder="0.00"
                                    value={basePayInput}
                                    onChange={e => {
                                        const raw = e.target.value;
                                        if (raw === '' || /^[\d.]*$/.test(raw)) setBasePayInput(raw);
                                    }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Typography sx={{ color: BRAND_LUXE_PRIMARY, fontWeight: 700, fontSize: 16 }}>$</Typography>
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={budgetFieldSx}
                                />
                            </UgcFormFieldCard>
                            <UgcFormFieldCard>
                                <Typography sx={labelSx}>Commission rate (%)</Typography>
                                <TextField
                                    fullWidth
                                    type="number"
                                    inputProps={{ min: 1, max: 100, step: 1 }}
                                    placeholder="20"
                                    value={commissionRateInput}
                                    onChange={e => setCommissionRateInput(e.target.value)}
                                    sx={budgetFieldSx}
                                />
                            </UgcFormFieldCard>
                            <UgcFormFieldCard>
                                <Typography sx={labelSx}>Display pool ($)</Typography>
                                <TextField
                                    fullWidth
                                    type="text"
                                    inputMode="decimal"
                                    placeholder="2000"
                                    value={displayPoolInput}
                                    onChange={e => {
                                        const raw = e.target.value;
                                        if (raw === '' || /^[\d.]*$/.test(raw)) setDisplayPoolInput(raw);
                                    }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Typography sx={{ color: BRAND_LUXE_PRIMARY, fontWeight: 700, fontSize: 16 }}>$</Typography>
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={budgetFieldSx}
                                />
                            </UgcFormFieldCard>
                        </Box>
                    )}

                    {feeBreakdown ? (
                        <Box
                            sx={{
                                bgcolor: BRAND_SURFACE_CONTAINER,
                                border: '1px solid rgba(255,201,101,0.18)',
                                borderRadius: 2,
                                p: 2.5,
                            }}>
                            <Typography
                                sx={{
                                    fontSize: 10,
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.14em',
                                    color: BRAND_LUXE_PRIMARY,
                                    mb: 1.25,
                                }}>
                                Budget breakdown
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: 13,
                                    fontFamily: FONT_ENUM.MAIN_REGULAR,
                                    color: 'rgba(255,255,255,0.75)',
                                    lineHeight: 1.65,
                                }}>
                                Min. creator pool ({tierSummaryLabel}):{' '}
                                {formatCurrency(feeBreakdown.minimumPerParticipant)} × {feeBreakdown.participantsUsed} ={' '}
                                {formatCurrency(feeBreakdown.minimumParticipantPay)}
                                <br />
                                {feeBreakdown.totalPlatformFee > 0 ? (
                                    <>
                                        Platform fees: {formatCurrency(feeBreakdown.platformFeePerPlatform)} ×{' '}
                                        {platforms.length} = {formatCurrency(feeBreakdown.totalPlatformFee)}
                                        <br />
                                    </>
                                ) : null}
                                Creators + platforms (minimum): {formatCurrency(feeBreakdown.calculatedMinimumTotal)}
                                {feeBreakdown.policyFloorApplied ? (
                                    <>
                                        <br />
                                        <Box component="span" sx={{ color: BRAND_LUXE_PRIMARY }}>
                                            {formatCurrency(ugcBudget.absolute_min_total_budget_usd)} campaign minimum
                                            applied
                                        </Box>
                                    </>
                                ) : null}
                                <br />
                                <Box component="span" sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, color: '#fff' }}>
                                    Minimum to fund: {formatCurrency(feeBreakdown.minimumRequiredBudget)}
                                </Box>
                                {budgetFormatted > 0 ? (
                                    <>
                                        <br />
                                        Allocated per creator (your total):{' '}
                                        {formatCurrency(feeBreakdown.actualPerParticipant)}
                                    </>
                                ) : null}
                                {belowMinimum ? (
                                    <>
                                        <br />
                                        <Box
                                            component="span"
                                            sx={{ color: '#f87171', fontFamily: FONT_ENUM.MAIN_MEDIUM }}>
                                            Below minimum — raise budget before duplicating.
                                        </Box>
                                    </>
                                ) : null}
                            </Typography>
                        </Box>
                    ) : isAppInstall ? (
                        <Box
                            sx={{
                                bgcolor: BRAND_SURFACE_CONTAINER,
                                border: '1px solid rgba(255,255,255,0.06)',
                                borderRadius: 2,
                                p: 2,
                            }}>
                            <Typography sx={{ fontSize: 13, color: BRAND_LUXE_MUTED, lineHeight: 1.5 }}>
                                App-install / CPA campaigns fund from available budget as installs clear. Set the
                                working budget you’ll top up for this run.
                            </Typography>
                        </Box>
                    ) : null}
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
                <Button onClick={onClose} disabled={submitting} sx={{ color: BRAND_LUXE_MUTED, textTransform: 'none' }}>
                    Cancel
                </Button>
                <Button
                    onClick={() => void handleSubmit()}
                    disabled={
                        submitting ||
                        belowMinimum ||
                        overCreatorLimit ||
                        budgetFormatted <= 0 ||
                        !String(startDate || '').trim() ||
                        !String(endDate || '').trim() ||
                        (campaign?.campaign_type !== 'commission' && !isRecurrent && !String(deliveryDate || '').trim())
                    }
                    variant="contained"
                    sx={{
                        bgcolor: BRAND_PRIMARY_CONTAINER,
                        color: BRAND_ON_PRIMARY_FIXED,
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        textTransform: 'none',
                        px: 2.5,
                        '&:hover': { bgcolor: BRAND_LUXE_PRIMARY },
                        '&.Mui-disabled': { bgcolor: '#6b6050', color: '#a89880' },
                    }}>
                    {submitting ? (
                        <CircularProgress size={18} sx={{ color: BRAND_ON_PRIMARY_FIXED }} />
                    ) : (
                        'Duplicate as draft'
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
